// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { parseLocalDate } from '@/utils/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageCircle, Package, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import VideoMessageThread from './VideoMessageThread';

interface VideoOrder {
  id: string;
  vendor_id: string;
  event_id: string;
  package_id: string;
  user_id: string;
  purchaser_name: string;
  purchaser_email: string;
  player_name: string;
  total_amount_cents: number;
  status: string;
  created_at: string;
  // Joined data
  vendor?: {
    business_name: string;
    user_id: string;
  };
  event?: {
    name: string;
  };
  package?: {
    package_type: string;
  };
}

interface VendorVideoMessagingProps {
  vendorId: string;
}

const VendorVideoMessaging: React.FC<VendorVideoMessagingProps> = ({ vendorId }) => {
  const [orders, setOrders] = useState<VideoOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<VideoOrder | null>(null);
  const [showMessageThread, setShowMessageThread] = useState(false);

  useEffect(() => {
    fetchVendorOrders();
  }, [vendorId]);

  const fetchVendorOrders = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_orders')
        .select(`
          *,
          vendor:video_vendor_profiles!vendor_id(business_name, user_id),
          event:events!event_id(name),
          package:video_packages!package_id(package_type)
        `)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching vendor orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const openMessageThread = (order: VideoOrder) => {
    setSelectedOrder(order);
    setShowMessageThread(true);
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading orders...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Customer Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="border border-border/50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{order.event?.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">{order.package?.package_type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {order.purchaser_name} (Player: {order.player_name})
                          </span>
                        </div>
                      </div>
                      <div className="text-right space-y-2">
                        <Badge className={getStatusColor(order.status)}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </Badge>
                        <p className="text-sm font-medium">{formatPrice(order.total_amount_cents)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t">
                      <p className="text-xs text-muted-foreground">
                        Order #{order.id.substring(0, 8)}...
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openMessageThread(order)}
                        className="flex items-center gap-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Messages
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Orders Yet</h3>
              <p className="text-muted-foreground">
                When customers place video orders, they'll appear here and you can message them directly.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Thread Modal */}
      <VideoMessageThread
        open={showMessageThread}
        onOpenChange={setShowMessageThread}
        order={selectedOrder}
        userType="vendor"
      />
    </>
  );
};

export default VendorVideoMessaging;